import {createAsyncThunk, createSelector, createSlice} from '@reduxjs/toolkit';
import api from '../../../common/api';
import {parseMoment} from '../../../common/utils/parser';
import {fetchOne as fetchOneInstalador} from '../reducer';

const initialState = {
  instaladorId: 0,
  byId: {},
  byVersao: {},
  selectedRowKeys: [],
  artefato: false,
};

export const byIdSelector = state => state.instaladoresArtefatos.byId;
export const byVersaoSelector = state => state.instaladoresArtefatos.byVersao;
export const selectedRowKeysSelector = state => state.instaladoresArtefatos.selectedRowKeys;
export const artefatoSelector = state => state.instaladoresArtefatos.artefato;

export const dataSourceSelector = createSelector(
    byIdSelector,
    byVersaoSelector,
    (byId, byVersao) =>
        Object.entries(byVersao)
            .map(([versao, ids]) => {
              let ativo = false;
              const artefatos = ids
                  .map(id => {
                    const artefato = byId[id];
                    ativo |= artefato.ativo;
                    return artefato;
                  });
              return {versao, ativo, artefatos};
            }, {}),
);

export const artefatoFormSelector = createSelector(
    artefatoSelector,
    artefato =>
        artefato ? ({
          ...artefato,
          dataCriacao: parseMoment(artefato.dataCriacao),
          dataAtualizacao: parseMoment(artefato.dataAtualizacao),
        }) : {},
);

export const fetch = createAsyncThunk(
    'instaladoresArtefatos/fetch',
    async (instaladorId, thunkAPI) => {
      const res = await api.get(`/instaladores/${instaladorId}/artefatos`);
      return res.data;
    },
);

export const removeSelectedRowKeys = createAsyncThunk(
    'instaladoresArtefatos/removeSelectedRowKeys',
    async (_, thunkAPI) => {
      try {
        const {instaladorId, byVersao, selectedRowKeys} = thunkAPI.getState().instaladoresArtefatos;
        await Promise.all(selectedRowKeys
            .flatMap(versao => byVersao[versao])
            .map(id => api.delete(`/instaladores/${instaladorId}/artefatos/${id}`)));
        await thunkAPI.dispatch(fetchOneInstalador(instaladorId));
      } catch (e) {
        return thunkAPI.rejectWithValue(e);
      }
    },
);

export const fetchOne = createAsyncThunk(
    'instaladoresArtefatos/fetchOne',
    async ({instaladorId, id}, thunkAPI) => {
      const res = await api.get(`/instaladores/${instaladorId}/artefatos/${id}`);
      return res.data;
    },
);

export const updateOne = createAsyncThunk(
    'instaladoresArtefatos/updateOne',
    async (payload, thunkAPI) => {
      try {
        const {instaladorId} = thunkAPI.getState().instaladoresArtefatos;
        const {id, ...artefato} = payload;
        await api.put(`/instaladores/${instaladorId}/artefatos/${id}`, artefato);
        await thunkAPI.dispatch(fetchOne({instaladorId, id}));
        await thunkAPI.dispatch(fetchOneInstalador(instaladorId));
      } catch (e) {
        return thunkAPI.rejectWithValue(e);
      }
    },
);

export const insertOne = createAsyncThunk(
    'instaladoresArtefatos/insertOne',
    async (payload, thunkAPI) => {
      try {
        const {instaladorId} = thunkAPI.getState().instaladoresArtefatos;
        const formData = new FormData();
        formData.append('arquivo', payload.arquivo);
        formData.append('arquiteturas', payload.arquiteturas);
        formData.append('versao', payload.versao);
        formData.append('ativo', payload.ativo);
        const headers = {};
        headers['content-type'] = 'multipart/form-data';
        const res = await api.post(`/instaladores/${instaladorId}/artefatos`, formData, {headers});
        const id = res.data.id;
        await thunkAPI.dispatch(fetchOne({instaladorId, id}));
        await thunkAPI.dispatch(fetch(instaladorId));
        await thunkAPI.dispatch(fetchOneInstalador(instaladorId));
        return res.data;
      } catch (e) {
        return thunkAPI.rejectWithValue(e);
      }
    },
);

export const slice = createSlice({
  name: 'instaladoresArtefatos',
  initialState,
  reducers: {
    clear: () => initialState,
    search: (state, action) => {
      state.search = action.payload;
    },
    onRowSelectionChange: (state, action) => {
      state.selectedRowKeys = action.payload;
    },
    clearOne: (state) => {
      state.artefato = false;
    },
    newOne: (state, action) => {
      state.instaladorId = action.payload.instaladorId;
      state.artefato = {
        versao: '',
        arquitetura: '',
        ativo: false,
      };
    },
  },
  extraReducers: {
    [fetch.fulfilled]: (state, action) => {
      state.instaladorId = action.meta.arg;
      const byVersao = {};
      state.byId = action.payload.reduce((pv, cv) => {
        pv[cv.id] = cv;

        if (byVersao.hasOwnProperty(cv.versao))
          byVersao[cv.versao].push(cv.id);
        else
          byVersao[cv.versao] = [cv.id];

        return pv;
      }, {});
      state.byVersao = byVersao;
    },
    [removeSelectedRowKeys.fulfilled]: (state, action) => {
      state.selectedRowKeys.forEach(versao => {
        state.byVersao[versao] = state.byVersao[versao]
            .forEach(id => {
              delete state.byId[id];
            });
        delete state.byVersao[versao];
      });
      state.selectedRowKeys = [];
    },
    [fetchOne.fulfilled]: (state, action) => {
      state.instaladorId = action.meta.arg.instaladorId;
      const {id, versao, arquitetura, ativo} = action.payload;
      const arquiteturas = arquitetura ? [arquitetura] : [];
      state.artefato = {...initialState.artefato, ...action.payload, arquiteturas};
      state.byId[id] = {...state.byId[id], id, versao, arquitetura, ativo};
      const byVersao = state.byVersao[versao];
      if (byVersao && !byVersao.some(a => a === id))
        byVersao.push(id);
    },
  },
});

export const {clear, search, onRowSelectionChange, clearOne, newOne} = slice.actions;

export default slice.reducer;
